import {
  estimateDeclineReasonLabel,
  type MyEstimateResponse,
  type MyEstimateResponseStatus,
} from '@massilia/queries'
import { Button, StatusBadge } from '@massilia/ui'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { Check, Download, MessageSquarePlus, X } from 'lucide-react'

import { pensionTypeLabels } from '@/components/booking-sections/pension-type-labels'
import { centsToEuros, formatPrice } from '@/utils/format-price'

/** Les quatre gestes possibles sur une proposition. `null` = lecture seule. */
export interface EstimateResponseActions {
  onAccept: () => void
  onDecline: () => void
  onDownloadPdf: () => void
  onRequestModification: () => void
}

interface EstimateResponseCardProps {
  actions: EstimateResponseActions | null
  isDownloadingPdf?: boolean
  response: MyEstimateResponse
}

interface ResponseStatusBadge {
  label: string
  tone: 'danger' | 'info' | 'neutral' | 'success' | 'warning'
}

const STATUS_BADGES = {
  AWAITING_CLIENT: { label: 'En attente de votre réponse', tone: 'info' },
  CONFIRMED: { label: 'Confirmée', tone: 'success' },
  DECLINED: { label: 'Refusée', tone: 'danger' },
  DECLINED_AUTO: { label: 'Déclinée automatiquement', tone: 'neutral' },
  EXPIRED: { label: 'Expirée', tone: 'neutral' },
  MODIFICATION_REQUESTED: { label: 'Modification demandée', tone: 'warning' },
  RESERVATION_REQUESTED: { label: 'Réservation demandée', tone: 'warning' },
} satisfies Record<MyEstimateResponseStatus, ResponseStatusBadge>

const formatDay = (iso: string) => format(new Date(iso), 'd MMMM yyyy', { locale: fr })

/**
 * Une proposition, telle que le client la lit. Écrite une fois : l'onglet Devis
 * et la page publique du devis (passe 2) rendent le même composant, seule la
 * source des mutations diffère.
 */
export function EstimateResponseCard({
  actions,
  isDownloadingPdf = false,
  response,
}: EstimateResponseCardProps) {
  const badge = STATUS_BADGES[response.status]

  return (
    // `id` : cible du `#<id>` posé par la page publique quand elle renvoie un
    // client connecté vers son espace.
    <article
      className="flex flex-col gap-4 border-t border-gray-light-200 p-4 first:border-t-0 md:p-6"
      id={response.id}
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-semibold text-gray-light-900">{response.name}</h3>
          <p className="text-xs text-gray-light-600">
            Valable jusqu&apos;au {formatDay(response.validTo)}
          </p>
        </div>
        <StatusBadge tone={badge.tone}>{badge.label}</StatusBadge>
      </header>

      {response.segments.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {response.segments.map((segment, index) => (
            <li className="text-sm text-gray-light-700" key={`${segment.departureAt}-${index}`}>
              <span className="font-medium">
                {segment.departureLocation} → {segment.arrivalLocation}
              </span>{' '}
              · {segment.airlineLabel}
            </li>
          ))}
        </ul>
      )}

      {response.hotels.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {response.hotels.map((hotel) => (
            <li className="text-sm text-gray-light-700" key={`${hotel.name}-${hotel.fromDate}`}>
              <span className="font-medium">{hotel.name}</span> · {hotel.location} ·{' '}
              {hotel.roomType} · du {formatDay(hotel.fromDate)} au {formatDay(hotel.toDate)}
              {hotel.pensionType && (
                <span className="block">{pensionTypeLabels.get(hotel.pensionType)}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {response.passengerPrices.length > 0 && (
        <ul className="flex flex-col gap-1">
          {response.passengerPrices.map((row) => (
            <li className="text-sm text-gray-light-700" key={row.label}>
              {row.count} × {row.label} à {formatPrice(centsToEuros(row.unitPrice))}
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <p className="text-lg font-semibold text-gray-light-900">
          {formatPrice(centsToEuros(response.totalPrice))}
        </p>
        {response.totalPriceWithInsurance > response.totalPrice && (
          <p className="text-xs text-gray-light-600">
            {formatPrice(centsToEuros(response.totalPriceWithInsurance))} avec assurance
          </p>
        )}
      </div>

      <p className="text-xs text-gray-light-600">
        {response.conditions.exchangeAllowed
          ? `Modifiable (${formatPrice(centsToEuros(response.conditions.exchangeFee))} de frais)`
          : 'Non modifiable'}{' '}
        ·{' '}
        {response.conditions.refundAllowed
          ? `Remboursable (${formatPrice(centsToEuros(response.conditions.refundFee))} de frais)`
          : 'Non remboursable'}
      </p>

      {response.declinedAt && response.declineReason && (
        <p className="text-sm text-gray-light-600">
          Refusée le {formatDay(response.declinedAt)} —{' '}
          {estimateDeclineReasonLabel(response.declineReason)}
          {response.declineComment && <> : « {response.declineComment} »</>}
        </p>
      )}

      {/* TOUTES les demandes, pas seulement la dernière : le client qui en
          enchaîne deux doit pouvoir relire ce qu'il avait écrit la première
          fois — l'agence, elle, les voit toutes. Et sous chacune, ce que
          l'agence a répondu : sans cela le client relit sa propre demande sans
          jamais retrouver la réponse reçue par mail. */}
      {response.modificationRequests.length > 0 && (
        <ul className="flex flex-col gap-3 text-sm">
          {response.modificationRequests.map((request) => (
            <li className="flex flex-col gap-2" key={request.id}>
              <p className="text-gray-light-600">
                Modification demandée le {formatDay(request.createdAt)} : « {request.message} »
              </p>

              {request.replies.map((reply) => (
                <div
                  className="flex flex-col gap-0.5 border-l-2 border-brand-600/30 pl-3"
                  key={reply.id}
                >
                  <p className="text-xs font-medium text-gray-light-600">
                    Réponse de notre agence — {formatDay(reply.createdAt)}
                  </p>
                  <p className="whitespace-pre-line text-gray-light-900">{reply.message}</p>
                </div>
              ))}
            </li>
          ))}
        </ul>
      )}

      {response.status === 'EXPIRED' && (
        <p className="text-sm text-gray-light-600">
          Cette proposition n&apos;est plus valable. Écrivez-nous, nous vous en établirons une
          nouvelle.
        </p>
      )}

      {actions && (
        <div className="flex flex-wrap gap-2">
          <Button
            className="gap-1.5"
            disabled={isDownloadingPdf}
            onClick={actions.onDownloadPdf}
            variant="outline"
          >
            <Download className="size-4" />
            Télécharger le devis
          </Button>

          {response.canAct && (
            <>
              <Button
                className="gap-1.5 bg-brand-600 text-white hover:bg-brand-700"
                onClick={actions.onAccept}
              >
                <Check className="size-4" />
                Confirmer
              </Button>
              <Button className="gap-1.5" onClick={actions.onRequestModification} variant="outline">
                <MessageSquarePlus className="size-4" />
                Demander des modifications
              </Button>
              <Button
                className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={actions.onDecline}
                variant="outline"
              >
                <X className="size-4" />
                Refuser
              </Button>
            </>
          )}
        </div>
      )}
    </article>
  )
}
